import { CartLine, CartState, DealCartLine, ItemCartLine, MenuItem, DrinkSelection, itemCartLineId, normalizeItemNotes } from './cartTypes';
import { isSaladSaucePreferenceValue, saladSaucePreferenceLabel } from '../../utils/saladSaucePreference';

const KEY = 'chunkzters_cart_v2';

function normalizeLine(raw: unknown): CartLine | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const quantity = Math.max(1, Number(row.quantity) || 1);

  if (row.kind === 'deal') {
    const dealId = typeof row.dealId === 'string' ? row.dealId : '';
    const dealName = typeof row.dealName === 'string' ? row.dealName : '';
    const dealPrice = Number(row.dealPrice);
    const lineId = typeof row.lineId === 'string' ? row.lineId : dealId;
    const selections = Array.isArray(row.selections)
      ? row.selections.filter(
          (s): s is DealCartLine['selections'][number] =>
            !!s &&
            typeof s === 'object' &&
            typeof (s as { slotId?: unknown }).slotId === 'string' &&
            typeof (s as { slotLabel?: unknown }).slotLabel === 'string' &&
            typeof (s as { menuItemId?: unknown }).menuItemId === 'string' &&
            typeof (s as { menuItemName?: unknown }).menuItemName === 'string'
        )
      : [];
    if (!dealId || !dealName || !Number.isFinite(dealPrice)) return null;
    const eligibleForDiscount = row.eligibleForDiscount === true;
    return {
      kind: 'deal',
      lineId,
      dealId,
      dealName,
      dealPrice,
      dealImageUrl: typeof row.dealImageUrl === 'string' ? row.dealImageUrl : undefined,
      selections,
      quantity,
      eligibleForDiscount,
    };
  }

  const item = row.item as MenuItem | undefined;
  if (!item || typeof item.id !== 'string' || typeof item.name !== 'string') return null;
  const drinkSelection =
    row.drinkSelection &&
    typeof row.drinkSelection === 'object' &&
    typeof (row.drinkSelection as DrinkSelection).menuItemId === 'string' &&
    typeof (row.drinkSelection as DrinkSelection).menuItemName === 'string'
      ? (row.drinkSelection as DrinkSelection)
      : undefined;
  const notes = normalizeItemNotes(typeof row.notes === 'string' ? row.notes : undefined);
  const isMeal = row.isMeal === true;
  const mealUpgradePrice = isMeal ? Number(row.mealUpgradePrice) || 0 : undefined;
  const flavourChoice =
    row.flavourChoice &&
    typeof row.flavourChoice === 'object' &&
    typeof (row.flavourChoice as { value?: unknown }).value === 'string' &&
    typeof (row.flavourChoice as { label?: unknown }).label === 'string'
      ? {
          value: (row.flavourChoice as { value: string }).value,
          label: (row.flavourChoice as { label: string }).label,
        }
      : undefined;
  let saladSaucePreference: { value: string; label: string } | undefined;
  if (
    row.saladSaucePreference &&
    typeof row.saladSaucePreference === 'object' &&
    typeof (row.saladSaucePreference as { value?: unknown }).value === 'string' &&
    typeof (row.saladSaucePreference as { label?: unknown }).label === 'string'
  ) {
    saladSaucePreference = {
      value: (row.saladSaucePreference as { value: string }).value,
      label: (row.saladSaucePreference as { label: string }).label,
    };
  } else if (typeof row.saladSaucePreference === 'string' && isSaladSaucePreferenceValue(row.saladSaucePreference)) {
    saladSaucePreference = {
      value: row.saladSaucePreference,
      label: saladSaucePreferenceLabel(row.saladSaucePreference),
    };
  }
  const lineId =
    typeof row.lineId === 'string'
      ? row.lineId
      : itemCartLineId(
          item.id,
          drinkSelection?.menuItemId,
          notes,
          isMeal,
          saladSaucePreference?.value,
          flavourChoice?.value
        );
  return {
    kind: 'item',
    lineId,
    item,
    quantity,
    drinkSelection,
    notes,
    isMeal,
    mealUpgradePrice,
    saladSaucePreference,
    flavourChoice,
  };
}

export function loadCart(): CartState {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return migrateLegacyCart();
    const parsed = JSON.parse(raw) as CartState;
    if (!parsed || !Array.isArray(parsed.lines)) return { lines: [] };
    const lines = parsed.lines.map(normalizeLine).filter((l): l is CartLine => l !== null);
    return {
      lines,
      promoCode: typeof parsed.promoCode === 'string' ? parsed.promoCode : undefined,
      promoDiscountAmount:
        parsed.promoDiscountAmount !== undefined ? Number(parsed.promoDiscountAmount) || 0 : undefined,
    };
  } catch {
    return { lines: [] };
  }
}

function migrateLegacyCart(): CartState {
  try {
    const legacyRaw = localStorage.getItem('chunkzters_cart_v1');
    if (!legacyRaw) return { lines: [] };
    const parsed = JSON.parse(legacyRaw) as { lines?: Array<{ item?: MenuItem; quantity?: number }> };
    if (!parsed?.lines?.length) return { lines: [] };
    const lines: ItemCartLine[] = parsed.lines
      .filter((l) => l?.item?.id)
      .map((l) => ({
        kind: 'item' as const,
        lineId: l.item!.id,
        item: l.item!,
        quantity: Math.max(1, Number(l.quantity) || 1),
      }));
    return { lines };
  } catch {
    return { lines: [] };
  }
}

export function saveCart(state: CartState): void {
  localStorage.setItem(KEY, JSON.stringify(state));
}
